import { useEffect } from 'react'
import type { NextPage } from 'next'
import { useQuery } from '@apollo/client'
import classNames from 'classnames'
import Template from 'templates/Hub'
import { getSellerLeadIndicationsSummary } from 'lib/partner'
import {
  SellerLeadIndicationSummaryQuery,
  SellerLeadIndicationSummaryQueryVariables,
} from 'graphql/types'
import GET_SELLER_LEAD_INDICATIONS_SUMMARY from 'graphql/queries/sellerLeadIndicationSummary'
import Button from 'components/Button'
import Banner from 'components/Banner'
import MenuIndicator from 'components/navigation/Menu/MenuIndicator'
import Loader from 'components/Loader'
import Typography from 'components/Typography'
import IconCirclePlus from 'icons/IconCirclePlus'
import useLogin from 'hooks/useLogin'
import Header from './components/Header'
import {
  clickCreateIndicationEvent,
  clickFocusRegionsEvent,
  clickSummaryCardEvent,
  openSummaryEvent,
  TIndication,
} from './events'
import styles from './MinhasIndicacoes.module.scss'
import FAQ from './components/FAQ'

const Indications: NextPage = (): JSX.Element => {
  const { data: summaryData, loading } = useQuery<
    SellerLeadIndicationSummaryQuery,
    SellerLeadIndicationSummaryQueryVariables
  >(GET_SELLER_LEAD_INDICATIONS_SUMMARY, {
    ssr: false,
  })
  const { userProfile } = useLogin()

  const getIndicationActionPath = () => {
    if (userProfile?.type === 'partner_broker')
      return '/corretor-parceiro/indicar'

    if (userProfile?.type === 'partner_referrer')
      return '/indique-imoveis/indicar'

    return ''
  }

  useEffect(() => {
    openSummaryEvent()
  }, [])

  return (
    <Template
      head={{
        title: 'Minhas indicações | EmCasa',
        description: 'Minhas indicações | EmCasa',
        url: '/minhas-indicacoes',
      }}
      checkUserType
    >
      <div className={styles.ecIndicationsPage}>
        <div className={styles.ecIndicationsPageGrid}>
          <div className={styles.ecIndicationsPageGridHeader}>
            <Header
              title="Minhas indicações"
              trailingContent={
                <Button
                  data-testid="ecIndicationsPageActionButton"
                  element="a"
                  href={getIndicationActionPath()}
                  theme="pink"
                  Icon={{
                    Component: IconCirclePlus,
                    title: 'Ícone: Check',
                    color: 'white',
                  }}
                  onClick={clickCreateIndicationEvent}
                >
                  Indicar imóvel
                </Button>
              }
            />
          </div>
          {loading ? (
            <Loader />
          ) : (
            <ul
              data-testid="ecIndicationsPageDashboard"
              className={classNames(
                styles.ecIndicationsPageDashBoard,
                styles.ecIndicationsPageGridMain,
              )}
            >
              {getSellerLeadIndicationsSummary(summaryData)?.map((item) => (
                <li key={item.status}>
                  <MenuIndicator
                    element="a"
                    {...item.content}
                    onClick={() =>
                      clickSummaryCardEvent(item.status as TIndication)
                    }
                  />
                </li>
              ))}
            </ul>
          )}

          <div className={styles.ecIndicationsPageGridSidebar}>
            <Banner
              noPadding
              onlyMobileLayout
              darkContainer
              button={{
                label: 'Consultar regiões',
                props: {
                  element: 'a',
                  theme: 'white',
                  href: '/perfil/minhas-indicacoes/regioes-foco',
                  onClick: clickFocusRegionsEvent,
                },
              }}
            >
              <>
                <Typography variant="ParagraphBig" bold>
                  Regiões foco do período
                </Typography>
                <Typography variant="ParagraphSmall">
                  Antes de indicar um imóvel, consulte aqui as regiões e valores
                  que são nosso foco de captação.
                </Typography>
              </>
            </Banner>
          </div>
        </div>
        <FAQ type={userProfile?.type} />
      </div>
    </Template>
  )
}

export default Indications
